import {
    Client,
    Crew,
    Event,
    SurveyMountingOrder,
    Project,
    Worker,
    MountingOrderVariant,
    FittingMountingOrder,
    MountingOrder
} from 'types/mounting.types';
import { api } from './api';
import { Author } from 'types/global.types';
import { File as CustomFile } from 'types/file.types';

export type MountingOrdersGet = {
    rows: MountingOrder[];
    authors: Author[];
};

export const mountingApi = api.injectEndpoints({
    endpoints: (builder) => ({
        fetchCalendarInformation: builder.query<Crew[], { userId: number; calendarId?: string }>({
            providesTags: ['MountingEvents'],
            query: ({ userId, calendarId }) => {
                const requestBody = {
                    user_id: userId,
                    calendar_id: calendarId
                };
                return { url: '/v1/mounting_schedule/load_calendar_information', method: 'POST', body: JSON.stringify(requestBody) };
            }
        }),
        syncGoogleCalendar: builder.mutation<any, { id: number; region?: string }>({
            query: (props) => {
                return { url: '/v1/mounting_schedule/sync_google_calendar', method: 'POST', body: JSON.stringify(props) };
            }
        }),
        fetchJobs: builder.query<
            { workers: Worker[]; clients: Client[]; projects: Project[]; events: Event[] },
            { region: any; userID: number }
        >({
            providesTags: ['MountingEvents'],
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/load_jobs',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        fetchEventDuration: builder.query<
            {
                events: {
                    id: string;
                    order_id: string;
                    title: string;
                    author: string;
                    client: string;
                    region: string;
                    comment: string;
                    address: string;
                    start_date: string;
                    end_date: string;
                    google_calendar_event_id: string;
                    local_id: string;
                }[];
                clientFiles: CustomFile[];
                factoryFiles: CustomFile[];
                designerFiles: CustomFile[];
                documentationFiles: CustomFile[];
            },
            { event_id: string }
        >({
            providesTags: ['MountingEvents'],
            query: ({ event_id }) => {
                return {
                    url: 'v1/mounting_schedule/check_event_duration',
                    method: 'POST',
                    body: JSON.stringify({ event_id })
                };
            }
        }),
        deleteMountingJob: builder.mutation<
            any,
            {
                selectedEvent: string;
                local_id: string;
            }
        >({
            invalidatesTags: ['MountingEvents'],

            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/delete_mounting_job',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        fetchFilesForEvent: builder.query<
            { clientFiles: CustomFile[]; factoryFiles: CustomFile[]; documentationFiles: CustomFile[]; designerFiles: CustomFile[] },
            { projectId: string; postId: number }
        >({
            providesTags: ['MountingEvents'],

            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/load_files_for_event',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        fetchFiles: builder.query<
            { clientFiles: CustomFile[]; factoryFiles: CustomFile[]; documentationFiles: CustomFile[]; designerFiles: CustomFile[] },
            { projectId: number }
        >({
            providesTags: ['MountingEvents'],

            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/load_files',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        fetchClientInfo: builder.query<any, { clientId: number; postId: number }>({
            providesTags: ['Clients'],
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/load_client_info',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        addOrUpdateJob: builder.mutation<any, any>({
            invalidatesTags: ['MountingEvents', 'Orders', 'MountingOrders'],
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/add_update_new_job',
                    method: 'POST',
                    body: JSON.stringify(props)
                };
            }
        }),
        sendMountingCreationNotification: builder.mutation<unknown, { orderId: number; startDate: string; endDate: string }>({
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/send_client_email',
                    method: 'POST',
                    body: JSON.stringify({ order_id: props.orderId, start_date: props.startDate, selected_date: props.endDate })
                };
            }
        }),
        sendMountingCreationNotificationClient: builder.mutation<unknown, { orderId: number; startDate: string; endDate: string }>({
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/client_assigned_mounting',
                    method: 'POST',
                    body: JSON.stringify({ order_id: props.orderId, start_date: props.startDate, selected_date: props.endDate })
                };
            }
        }),
        sendMountingDeletionNotification: builder.mutation<unknown, { mountingId: string; orderId: number }>({
            query: (props) => {
                return {
                    url: '/v1/mounting_schedule/send_client_email_deleted_mounting',
                    method: 'POST',
                    body: JSON.stringify({
                        mounting_id: props.mountingId,
                        order_id: props.orderId
                    })
                };
            }
        }),
        fetchMountingOrders: builder.query<MountingOrdersGet, undefined>({
            providesTags: ['MountingOrders'],
            query: () => {
                return {
                    url: '/v1/mounting_orders',
                    method: 'GET'
                };
            }
        }),
        createMountingOrderByOrderId: builder.mutation<
            { mounting_order_id: number },
            { orderId: number; userId: number; variant: MountingOrderVariant }
        >({
            invalidatesTags: ['MountingOrders'],
            query: ({ orderId, userId, variant }) => {
                return {
                    url: '/v1/mounting_orders/order_id',
                    method: 'POST',
                    body: { order_id: orderId, user_id: userId, variant }
                };
            }
        }),
        fetchMountingOrderByOrderId: builder.query<
            { survey: SurveyMountingOrder | null; fitting: FittingMountingOrder | null },
            { orderId: number; userId: number }
        >({
            providesTags: ['MountingOrders'],
            query: ({ orderId, userId }) => {
                return {
                    url: '/v1/mounting_orders/get_by_order_id',
                    method: 'POST',
                    body: { order_id: orderId, user_id: userId }
                };
            }
        }),
        fetchMountingOrderById: builder.query<SurveyMountingOrder | FittingMountingOrder, { mountingOrderId: number; userId: number }>({
            providesTags: ['MountingOrders'],
            query: ({ mountingOrderId, userId }) => {
                return {
                    url: '/v1/mounting_orders/get_by_id',
                    method: 'POST',
                    body: { id: mountingOrderId, user_id: userId }
                };
            }
        }),
        updateMountingOrderById: builder.mutation<undefined, SurveyMountingOrder>({
            invalidatesTags: ['MountingOrders'],
            query: (props) => {
                return {
                    url: '/v1/mounting_orders/update_by_id',
                    method: 'PUT',
                    body: props
                };
            }
        }),
        deleteMountingOrdersByIds: builder.mutation<undefined, { mountingOrdersIds: number[] }>({
            invalidatesTags: ['MountingOrders'],
            query: (props) => {
                return {
                    url: '/v1/mounting_orders',
                    method: 'delete',
                    body: { mounting_orders_ids: props.mountingOrdersIds }
                };
            }
        })

        // deleteMountingOrderFormFileByid: builder.mutation<undefined, { mountingOrderId: number }>({
        //     invalidatesTags: ['OrderSurveyFiles', 'MountingOrders'],
        //     query: ({ mountingOrderId }) => {
        //         return {
        //             url: '/v1/mounting_orders/delete_form_file_by_id',
        //             method: 'POST',
        //             body: { id: mountingOrderId }
        //         };
        //     }
        // }),
        // uploadMountingOrderFormFileByid: builder.mutation<undefined, { mountingOrderId: number; uploadedFile: File }>({
        //     query: ({ mountingOrderId, uploadedFile }) => {
        //         const formData = new FormData();
        //         formData.append('id', mountingOrderId.toString());
        //         if (uploadedFile) formData.append(`uploaded_files${0}`, uploadedFile);
        //         return { url: '/v1/mounting_orders/upload_form_file_by_id', method: 'POST', body: formData };
        //     },
        //     invalidatesTags: ['OrderSurveyFiles', 'MountingOrders']
        // })
    })
});

export const {
    useFetchCalendarInformationQuery,
    useSyncGoogleCalendarMutation,
    useFetchJobsQuery,
    useLazyFetchJobsQuery,
    useLazyFetchEventDurationQuery,
    useFetchEventDurationQuery,
    useLazyFetchFilesForEventQuery,
    useLazyFetchFilesQuery,
    useLazyFetchClientInfoQuery,
    useDeleteMountingJobMutation,
    useAddOrUpdateJobMutation,
    useSendMountingCreationNotificationMutation,
    useSendMountingCreationNotificationClientMutation,
    useSendMountingDeletionNotificationMutation,
    useFetchMountingOrdersQuery,
    useCreateMountingOrderByOrderIdMutation,
    useFetchMountingOrderByOrderIdQuery,
    useFetchMountingOrderByIdQuery,
    useUpdateMountingOrderByIdMutation,
    useDeleteMountingOrdersByIdsMutation
} = mountingApi;
